import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  businessRoles,
  businessRoleLabels,
  businessCapabilities,
  departmentalRoles,
  businessPolicySchema,
  type BusinessRole,
  type BusinessPolicy,
  type BusinessCapability,
} from "@payangel/shared";
import { useApi } from "../api";
import type { AccessGrant } from "../types";
import { Card, Loading, ErrorNotice } from "./Ui";

const labels: Record<BusinessCapability, string> = {
  submit: "Create and submit requests",
  edit: "Edit requests and add updates",
  "score.view": "View RICE scores and rankings",
  "score.edit": "Edit scoring",
  decide: "Approve, reject and change review stage",
  "delivery.update": "Change delivery status",
  "governance.view": "View governance and Board details",
  "governance.manage": "Manage escalation",
  export: "Export portfolio reports",
  "system.manage": "Manage accounts, roles and system settings",
};
export function BusinessRoles() {
  const api = useApi();
  const policy = useQuery({
    queryKey: ["business-policy"],
    queryFn: () => api.get<{ policy: BusinessPolicy }>("/permissions/rollout/"),
  });
  const accounts = useQuery({
    queryKey: ["access-grants", false],
    queryFn: () =>
      api.get<{ items: AccessGrant[] }>(
        "/admin/access-grants?includeArchived=false",
      ),
  });
  return (
    <Card className="business-roles">
      <span className="access-step">Step 2 · Assign access</span>
      <h2>Rollout roles and departments</h2>
      <p className="section-help">Select an authorised account, choose its business roles and department, then save with a reason.</p>
      {policy.data && <div className="access-policy-status">
        <strong>{policy.data.policy.requireAssignments ? "Business roles required" : "Migration mode — legacy access still enabled"}</strong>
        <span>{policy.data.policy.requireAssignments ? "Unassigned accounts see an access-pending screen." : "Assign pilot users before enabling mandatory business roles under Configure role permissions."}</span>
      </div>}
      <details className="access-guidance">
        <summary>How roles and rollout access work</summary>
      <p className="section-help">
        Chief of Staff and S&E Analyst default to full portfolio and system administration
        access, including managing users and settings. S&E Analyst acts as deputy
        to Chief of Staff; the separate Analyst Deputy role is retired. Champions and their deputies
        share submission and delivery-update rights within their departments.
        CEO/MD, Board and department heads default to read-only. Other business
        roles need a separate Administrator assignment to manage users and settings.
      </p>
      <p className="section-help">
        During migration, unassigned accounts retain legacy access. Before
        inviting pilot users, assign your own Administrator role and enable
        “Require rollout roles” below: unassigned accounts will then see only an
        access-pending welcome screen. Assigned roles replace legacy privileges. No accounts are
        assigned automatically.
      </p>
      </details>
      {policy.isPending || accounts.isPending ? (
        <Loading />
      ) : policy.isError || accounts.isError ? (
        <ErrorNotice
          error={
            policy.error ?? accounts.error ?? new Error("Unable to load roles")
          }
        />
      ) : (
        <BusinessRoleForm
          key={policy.data.policy.version}
          initial={policy.data.policy}
          accounts={accounts.data.items}
        />
      )}
      <InvitationStatus />
    </Card>
  );
}
function InvitationStatus() {
  const api = useApi();
  const query = useQuery({
    queryKey: ["role-invitations"],
    queryFn: () => api.get<{ enabled: boolean; items: Array<{ id: string; status: string; lastError: string | null; payload: { email: string; providerStatus?: string } }> }>("/permissions/rollout/invitations"),
    refetchInterval: 30000,
  });
  return <section className="access-invitations" aria-label="Invitation delivery">
    <span className="access-step">Step 3 · Track invitation</span>
    <h3>Invitation delivery</h3>
    <p className="section-help">An invitation is queued on the first business-role assignment, not when an account is added. Delivery waits until the role’s rollout phase opens. Provider acceptance does not confirm inbox delivery.</p>
    {query.isPending ? <Loading /> : query.isError ? <ErrorNotice error={query.error} /> : <>
      <p>{query.data.enabled ? "Mandrill delivery enabled" : "Email delivery disabled — invitations remain queued until configuration is ready."}</p>
      {query.data.items.length === 0 ? <p>No invitations queued yet.</p> : <div className="table-wrap"><table className="initiative-table">
        <thead><tr><th>Account</th><th>Status</th><th>Details</th></tr></thead>
        <tbody>{query.data.items.slice(0, 20).map(item => <tr key={item.id}><td>{item.payload.email}</td><td>{item.status === "sent" ? "Accepted by Mandrill" : item.status}</td><td>{item.lastError ?? item.payload.providerStatus ?? "Awaiting delivery"}</td></tr>)}</tbody>
      </table></div>}
    </>}
  </section>;
}
function BusinessRoleForm({
  initial,
  accounts,
}: {
  initial: BusinessPolicy;
  accounts: AccessGrant[];
}) {
  const api = useApi(),
    client = useQueryClient();
  const configurableRoles = businessRoles.filter(role => role !== "analyst_deputy" || initial.assignments.some(a => a.roles.includes(role)));
  const [policy, setPolicy] = useState(initial),
    [email, setEmail] = useState(""),
    [roles, setRoles] = useState<BusinessRole[]>([]),
    [departments, setDepartments] = useState(""),
    [reason, setReason] = useState(""),
    [error, setError] = useState<unknown>(null);
  const save = useMutation({
    mutationFn: (next: BusinessPolicy) =>
      api.put("/permissions/rollout/", {
        policy: businessPolicySchema.parse(next),
        changeReason: reason,
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["business-policy"] });
      await client.invalidateQueries({ queryKey: ["permissions-me"] });
      await client.invalidateQueries({ queryKey: ["session"] });
      client.removeQueries({ queryKey: ["initiatives"] });
      client.removeQueries({ queryKey: ["dashboard-summary"] });
    },
  });
  const select = (value: string) => {
    setEmail(value);
    const a = policy.assignments.find((a) => a.email === value);
    setRoles(a?.roles ?? []);
    setDepartments(a?.departments.join(", ") ?? "");
  };
  const submit = (next: BusinessPolicy) => {
    setError(null);
    try {
      businessPolicySchema.parse(next);
      save.mutate(next);
    } catch (e) {
      setError(e);
    }
  };
  return (
    <div className="portfolio-form">
      <form
        className="access-role-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit({
            ...policy,
            assignments: [
              ...policy.assignments.filter((a) => a.email !== email),
              {
                email,
                roles,
                departments: departments
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              },
            ],
          });
        }}
      >
        <label>
          Authorised account
          <select
            required
            value={email}
            onChange={(e) => select(e.target.value)}
          >
            <option value="">Select an active account</option>
            {accounts
              .filter((a) => a.active)
              .map((a) => (
                <option key={a.id} value={a.email}>
                  {a.userDisplayName || a.displayName || a.email} — {a.email}
                </option>
              ))}
          </select>
        </label>
        <fieldset className="access-role-options">
          <legend>Assigned roles (multiple allowed)</legend>
          {businessRoles.filter(role => role !== "analyst_deputy" || roles.includes(role)).map((role) => (
            <label key={role} className={roles.includes(role) ? "role-option selected" : "role-option"}>
              <input
                type="checkbox"
                checked={roles.includes(role)}
                onChange={(e) =>
                  setRoles((v) =>
                    e.target.checked
                      ? [...v, role]
                      : v.filter((r) => r !== role),
                  )
                }
              />
              {businessRoleLabels[role]}
            </label>
          ))}
        </fieldset>
        <label>
          Departments (comma separated; match the initiative Team exactly)
          <input
            required={roles.some((r) => departmentalRoles.has(r))}
            value={departments}
            onChange={(e) => setDepartments(e.target.value)}
            placeholder="Engineering, Finance"
          />
        </label>
        <p className="section-help">
          Department roles require a department. Combining a global business
          role with a department role grants global visibility; adding
          Administrator alone does not broaden portfolio visibility.
        </p>
        <label>
          Change reason
          <input
            required
            minLength={3}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <button
          className="btn-primary"
          disabled={save.isPending || !email || !roles.length}
        >
          {save.isPending ? "Saving…" : "Save role assignment"}
        </button>
      </form>
      <details>
        <summary>Configure role permissions</summary>
        <p>
          Changes affect every account assigned to a role. System administration
          is available to Administrator, Chief of Staff and S&E Analyst. Department boundaries cannot be widened by
          these checkboxes.
        </p>
        <label>
          <input
            type="checkbox"
            checked={Boolean(policy.requireAssignments)}
            onChange={(e) =>
              setPolicy((v) => ({ ...v, requireAssignments: e.target.checked }))
            }
          />
          Require rollout roles for every account (enable before pilot
          invitations)
        </label>
        <p className="section-help">
          This blocks all unassigned accounts, including legacy administrators.
          Assign your own Administrator role first. Revoke access to disable a
          user; migrated assignments cannot be removed to regain legacy access.
        </p>
        <div className="table-wrap">
          <table className="initiative-table">
            <thead>
              <tr>
                <th>Permission</th>
                {configurableRoles.map((r) => (
                  <th key={r}>{businessRoleLabels[r]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {businessCapabilities.map((c) => (
                <tr key={c}>
                  <td>{labels[c]}</td>
                  {configurableRoles.map((r) => (
                    <td key={r}>
                      <input
                        type="checkbox"
                        aria-label={`${businessRoleLabels[r]}: ${labels[c]}`}
                        checked={policy.roles[r].includes(c)}
                        disabled={
                          r === "administrator"
                            ? c !== "system.manage"
                            : (c === "system.manage" && r !== "chief_of_staff" && r !== "analyst") ||
                              (departmentalRoles.has(r) &&
                                [
                                  "score.edit",
                                  "decide",
                                  "governance.view",
                                  "governance.manage",
                                  "export",
                                ].includes(c))
                        }
                        onChange={(e) =>
                          setPolicy((v) => ({
                            ...v,
                            roles: {
                              ...v.roles,
                              [r]: e.target.checked
                                ? [...v.roles[r], c]
                                : v.roles[r].filter((p) => p !== c),
                            },
                          }))
                        }
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          className="btn-primary"
          disabled={save.isPending || reason.trim().length < 3}
          onClick={() => submit(policy)}
        >
          Save permission policy
        </button>
        <p className="section-help">
          Enter a change reason above before saving.
        </p>
      </details>
      {Boolean(error || save.isError) && (
        <ErrorNotice
          error={
            error instanceof Error
              ? error
              : (save.error ?? new Error("Unable to save roles"))
          }
        />
      )}
      {save.isSuccess && (
        <p role="status">
          Roles saved. Access is enforced on the next API request.
        </p>
      )}
    </div>
  );
}
